import { FedTier } from 'shared/models/env';
import { DEVSITE_LOCALSTORAGE, getLocalStorage } from 'modules/common/util/DevsiteLocalStorage';
import { userMetricsEnabled } from 'modules/common/util/userMetricsPreference';
import DevsiteEnv from 'modules/core/DevsiteEnv';
import { INTERNAL_FEATURE, OFF, ON } from './toggles-constants';

/**
 * See [our docs](https://docs.workday.build/wcp/developer-site/features/feature-flags) for more information on feature toggling in our application.
 */

// display of feature toggles
export const TOGGLE_SHOW_INTERNAL_FEATURES_AS_ON = INTERNAL_FEATURE;
export const TOGGLE_INTERNAL_FEATURES_SWITCHER = DevsiteEnv.IS_INTERNAL_ENV; // special toggle — most toggles should be driven by confidence level
export const TOGGLE_NONPROD_ENVIRONMENT_LABEL = DevsiteEnv.IS_INTERNAL_ENV; // special toggle — most toggles should be driven by confidence level
export const TOGGLE_ACCESS_POLICIES_SANDBOX = INTERNAL_FEATURE;

// server-related toggles (see shared/utils/resolveAuthEnvConfig.js for defaults)
export const BFF_ENABLED = DevsiteEnv.BFF_ENABLED;
export const HTTP_ONLY_AUTH_ENABLED = DevsiteEnv.HTTP_ONLY_AUTH_ENABLED;
export const GATEWAY_ROUTED_API_ENABLED = DevsiteEnv.GATEWAY_ROUTED_API_ENABLED;

// federal toggles
export const FEDERAL_BANNER_ENABLED = DevsiteEnv.FED_MODE;
export const FEDERAL_MODE = DevsiteEnv.FED_MODE;
export const IL4_MODE = INTERNAL_FEATURE && DevsiteEnv.FED_TIER === FedTier.IL4;

// monitoring and logging toggles
export const TOGGLE_DETAILED_BROWSER_LOGGING =
  // the only way to change the localstorage property is manually
  // we'll see if this ever gets used but the localstorage logic here might be a candidate for removal in the future
  INTERNAL_FEATURE || getLocalStorage(DEVSITE_LOCALSTORAGE.DETAILED_CONSOLE_LOGS);

// ux toggles
export const TOGGLE_CREATE_MULTIPLE_TENANTS = INTERNAL_FEATURE;
export const TOGGLE_PROMOTIONS_TAB_FEATURE_TOUR = OFF;
export const TOGGLE_DEV_FORUM_LINK = !!DevsiteEnv.FORUM_URL;
export const TOGGLE_DEV_FORUM_API = !!DevsiteEnv.DISCOURSE_ENABLED;
export const TOGGLE_TENANT_SIDE_PANEL = INTERNAL_FEATURE;
export const TOGGLE_ALLOW_MANAGING_WORKDAY_USERS = !DevsiteEnv.OCTO_OKTA_INTEGRATION;
export const TOGGLE_ALLOW_DELETING_USERS = DevsiteEnv.IS_INTERNAL_ENV;

// app console toggles
export const TOGGLE_APP_CONSOLE_BRANCH = INTERNAL_FEATURE;
export const TOGGLE_DEPLOY_INTEGRATIONS_TO_IMPL = ON;

// API Tooling toggles
export const TOGGLE_API_DISCOVERY_CENTER = DevsiteEnv.API_DISCOVERY_CENTER_ENABLED;
export const TOGGLE_MFE_INTERNAL_FEATURES = INTERNAL_FEATURE;
export const TOGGLE_MCP_EXPLORER = DevsiteEnv.MCP_EXPLORER_ENABLED;
export const TOGGLE_ODP_FOR_EXPLORERS = ON; // TODO refer to APITOOLS-5343 for required cleanup jiras

// Temporarily force-disabled via the leading `OFF`; the original conditions are preserved
// so Sentry can be re-enabled later by simply removing the `OFF &&` prefix.
export const TOGGLE_SENTRY = OFF && DevsiteEnv.SENTRY?.ENABLED && userMetricsEnabled();

// Incoming Features
export const TOGGLE_NEW_APPS_LIST = INTERNAL_FEATURE;
export const TOGGLE_LANGUAGE_SELECTOR = INTERNAL_FEATURE;
export const TOGGLE_PSEUDO_LANGUAGE_OPTIONS = INTERNAL_FEATURE;
export const TOGGLE_FRENCH_CANADIAN_LANGUAGE = INTERNAL_FEATURE;

export const TOGGLE_SEARCH_HIGHLIGHT = INTERNAL_FEATURE;

export const TOGGLE_STUDIO_BUILDER = !FEDERAL_MODE;
export const TOGGLE_STUDIO_INTERNAL_FEATURES = !FEDERAL_MODE;

export const TOGGLE_DRAFT_DOCS = DevsiteEnv.IS_INTERNAL_ENV;
export const TOGGLE_TRUSTARC_CCM = DevsiteEnv.IS_INTERNAL_ENV;

// explorers / marketing toggles
export const PUBLIC_DOCUMENTATION_ENABLED = DevsiteEnv.PUBLIC_RESOURCES_ENABLED;

/**
 * These are special toggles! DO NOT CHANGE unless you have aligned with the relevant team.
 *
 * Someday we would love to use a toggle service to manage these toggles, but for now we are passing around this object.
 *
 * Usage: Entire object is passed to MFEs. MFE can then access via toggles.TOGGLE_GLOBAL_CONNECT_TO_TENANT
 */
export const crossTeamToggles = {
  // referenced by SOAP API Explorer https://ghe.megaleo.com/wcp/soap-api-explorer/search?q=devSiteToggles
  TOGGLE_GLOBAL_CONNECT_TO_TENANT: ON,
  // referenced by SOAP API Explorer https://ghe.megaleo.com/wcp/soap-api-explorer/search?q=devSiteToggles
  INTERNAL_FEATURE,
  // the API Discover Center also receives this object for historical reasons but it does not appear to use it https://ghe.megaleo.com/wcp/api-discovery-hub/search?q=devSiteToggles
  TOGGLE_ODP_FOR_EXPLORERS, // TODO: refer to APITOOLS-5343 for required cleanup jiras
};
